import { useEffect, useRef, useState, type ReactNode } from 'react';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  /** Desktop max width. Mobile is always full-width. */
  maxWidth?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

const MAX_W = { sm: 'sm:max-w-sm', md: 'sm:max-w-lg', lg: 'sm:max-w-2xl' };

// iOS-style presentation: a bottom sheet on mobile (slide up, grab handle, swipe-down/backdrop
// to dismiss, safe-area inset) and a centered dialog on desktop. The standard modal wrapper for
// new screens — pass content as children.
export function BottomSheet({ isOpen, onClose, title, maxWidth = 'md', children }: BottomSheetProps) {
  const startY = useRef<number | null>(null);
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // lock background scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [isOpen, onClose]);

  // reset drag when (re)opened
  useEffect(() => { if (isOpen) { setDrag(0); setDragging(false); } }, [isOpen]);

  if (!isOpen) return null;

  // Swipe-down-to-dismiss (mobile): grab the handle/header area and pull down.
  const onTouchStart = (e: React.TouchEvent) => { startY.current = e.touches[0].clientY; setDragging(true); };
  const onTouchMove = (e: React.TouchEvent) => {
    if (startY.current == null) return;
    const delta = e.touches[0].clientY - startY.current;
    setDrag(Math.max(0, delta));
  };
  const onTouchEnd = () => {
    setDragging(false);
    if (drag > 110) onClose();
    else setDrag(0);
    startY.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      style={{ opacity: Math.max(0.35, 1 - drag / 300) }}
      onClick={onClose}
    >
      <div
        className={`ios-sheet-enter bg-white w-full ${MAX_W[maxWidth]} rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col overflow-hidden`}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          transform: drag ? `translateY(${drag}px)` : undefined,
          transition: dragging ? 'none' : 'transform 0.3s cubic-bezier(0.32,0.72,0,1)',
        }}
      >
        {/* grab handle + draggable header zone (mobile only) */}
        <div
          className="sm:cursor-default"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="sm:hidden mx-auto mt-3 h-1.5 w-10 rounded-full bg-gray-300 flex-shrink-0" />

          {title && (
            <div className="flex items-center justify-between px-5 pt-4 pb-3 sm:pt-5">
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
              <button
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center -mr-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 active:scale-90 transition"
                aria-label="Close"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 pt-2 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:pb-5">
          {children}
        </div>
      </div>
    </div>
  );
}
